<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useRouter } from 'vue-router';
import { useAccount } from 'dashboard/composables/useAccount';
import FormCard from '../components/FormCard.vue';
import EmbedCodeModal from '../components/FormBuilder/EmbedCodeModal.vue';

const store = useStore();
const router = useRouter();
const { accountScopedRoute } = useAccount();

const forms = useMapGetter('forms/getAll');
const publishedForms = useMapGetter('forms/getPublished');
const draftForms = useMapGetter('forms/getDrafts');
const archivedForms = useMapGetter('forms/getArchived');
const uiFlags = useMapGetter('forms/getUIFlags');

const activeTab = ref('all');
const search = ref('');
const deletingId = ref(null);
const embedFormId = ref(null);

const tabs = [
 { key: 'all', label: 'Todos' },
 { key: 'published', label: 'Publicados' },
 { key: 'draft', label: 'Rascunhos' },
 { key: 'archived', label: 'Arquivados' },
];

const tabForms = computed(() => {
 const map = {
 all: forms.value,
 published: publishedForms.value,
 draft: draftForms.value,
 archived: archivedForms.value,
 };
 return map[activeTab.value] || forms.value;
});

const filteredForms = computed(() => {
 const list = tabForms.value || [];
 const q = search.value.trim().toLowerCase();
 if (!q) return list;
 return list.filter(
 f =>
 f.name?.toLowerCase().includes(q) ||
 f.description?.toLowerCase().includes(q)
 );
});

const tabCounts = computed(() => ({
 all: forms.value?.length || 0,
 published: publishedForms.value?.length || 0,
 draft: draftForms.value?.length || 0,
 archived: archivedForms.value?.length || 0,
}));

const deletingForm = computed(() => {
 if (!deletingId.value || !forms.value) return null;
 return forms.value.find(f => f.id === deletingId.value);
});

const onNewForm = () => router.push(accountScopedRoute('forms_new'));

const onEdit = id =>
 router.push(accountScopedRoute('forms_edit', { formId: id }));

const onViewSubmissions = id =>
 router.push(accountScopedRoute('forms_submissions', { formId: id }));

const onDuplicate = async id => {
 await store.dispatch('forms/duplicate', id);
};

const onPublish = async id => {
 await store.dispatch('forms/publish', id);
};

const onArchive = async id => {
 await store.dispatch('forms/archive', id);
};

const onEmbed = id => {
 embedFormId.value = id;
};

const onDelete = id => {
 deletingId.value = id;
};

const confirmDelete = async () => {
 if (!deletingId.value) return;
 await store.dispatch('forms/delete', deletingId.value);
 deletingId.value = null;
};

const cancelDelete = () => {
 deletingId.value = null;
};

onMounted(() => {
 store.dispatch('forms/get');
});
</script>

<template>
 <div class="flex flex-col h-full w-full bg-n-solid-1">
 <!-- Header -->
 <div
 class="flex items-center justify-between px-6 py-4 border-b border-n-weak"
 >
 <div class="flex items-center gap-3">
 <div
 class="flex items-center justify-center size-8 rounded-lg bg-woot-50 dark:bg-woot-500/10"
 >
 <span
 class="i-lucide-file-text size-4 text-woot-600 dark:text-woot-400"
 />
 </div>
 <div>
 <h1
 class="text-lg font-semibold text-n-slate-12"
 >
 Formularios
 </h1>
 <p
 v-if="forms?.length"
 class="text-xs text-n-slate-10 !mb-0"
 >
 {{ forms.length }}
 {{ forms.length === 1 ? 'formulario' : 'formularios' }}
 </p>
 </div>
 </div>
 <div class="flex items-center gap-3">
 <!-- Search -->
 <div class="relative">
 <span
 class="i-lucide-search size-4 text-n-slate-9 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none"
 />
 <input
 v-model="search"
 type="text"
 placeholder="Buscar formulario..."
 class="reset-base h-9 w-56 pl-8 pr-3 text-sm rounded-lg bg-n-slate-2 border border-n-weak text-n-slate-12 placeholder:text-n-slate-9 focus:outline-none focus:ring-2 focus:ring-woot-500/30 focus:border-woot-500"
 />
 </div>
 <button
 class="flex items-center gap-2 px-4 py-2 bg-woot-500 text-white rounded-lg text-sm font-medium hover:bg-woot-600 transition-colors shadow-sm"
 @click="onNewForm"
 >
 <span class="i-lucide-plus size-4" />
 Criar formulario
 </button>
 </div>
 </div>

 <!-- Tabs -->
 <div
 class="flex items-center gap-1 px-6 pt-3 pb-0 border-b border-n-weak"
 >
 <button
 v-for="tab in tabs"
 :key="tab.key"
 class="px-3 py-2 text-sm font-medium rounded-t-lg transition-colors relative"
 :class="
 activeTab === tab.key
 ? 'text-woot-600 dark:text-woot-400 bg-woot-50/50 dark:bg-woot-500/5'
 : 'text-n-slate-10 hover:text-n-slate-12'
 "
 @click="activeTab = tab.key"
 >
 {{ tab.label }}
 <span
 class="ml-1.5 text-xs px-1.5 py-0.5 rounded-full"
 :class="
 activeTab === tab.key
 ? 'bg-woot-100 dark:bg-woot-500/20 text-woot-700 dark:text-woot-300'
 : 'bg-n-slate-3 text-n-slate-10'
 "
 >
 {{ tabCounts[tab.key] }}
 </span>
 <span
 v-if="activeTab === tab.key"
 class="absolute bottom-0 left-0 right-0 h-0.5 bg-woot-500 rounded-full"
 />
 </button>
 </div>

 <!-- Content -->
 <div class="flex-1 overflow-y-auto p-6">
 <!-- Loading -->
 <div
 v-if="uiFlags.isFetching && !forms?.length"
 class="flex items-center justify-center py-16"
 >
 <div
 class="size-8 border-2 border-woot-500 border-t-transparent rounded-full animate-spin"
 />
 </div>

 <!-- Empty state -->
 <div
 v-else-if="!forms?.length"
 class="flex flex-col items-center justify-center h-full text-center px-6"
 >
 <div
 class="flex items-center justify-center size-16 rounded-2xl bg-n-slate-3 mb-4"
 >
 <span class="i-lucide-file-text size-8 text-n-slate-9" />
 </div>
 <h3
 class="text-base font-semibold text-n-slate-12 mb-1"
 >
 Nenhum formulario criado
 </h3>
 <p
 class="text-sm text-n-slate-10 !mb-4 max-w-sm"
 >
 Crie seu primeiro formulário para capturar leads e informações de
 contatos.
 </p>
 <button
 class="flex items-center gap-2 px-4 py-2 bg-woot-500 text-white rounded-lg text-sm font-medium hover:bg-woot-600 transition-colors"
 @click="onNewForm"
 >
 <span class="i-lucide-plus size-4" />
 Criar formulario
 </button>
 </div>

 <!-- No search results -->
 <div
 v-else-if="filteredForms.length === 0"
 class="flex flex-col items-center justify-center py-20 text-center"
 >
 <span
 class="i-lucide-search-x size-10 text-n-slate-7 mb-3"
 />
 <p class="text-sm text-n-slate-10 !mb-0">
 Nenhum formulario encontrado para "<span class="font-medium">{{
 search
 }}</span>"
 </p>
 </div>

 <!-- Grid -->
 <div
 v-else
 class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4"
 >
 <FormCard
 v-for="form in filteredForms"
 :key="form.id"
 :form="form"
 @edit="onEdit"
 @duplicate="onDuplicate"
 @publish="onPublish"
 @archive="onArchive"
 @embed="onEmbed"
 @delete="onDelete"
 @view-submissions="onViewSubmissions"
 />
 </div>
 </div>

 <!-- Delete confirmation modal -->
 <Teleport to="body">
 <Transition
 enter-active-class="transition-opacity duration-200"
 leave-active-class="transition-opacity duration-150"
 enter-from-class="opacity-0"
 leave-to-class="opacity-0"
 >
 <div
 v-if="deletingId"
 class="fixed inset-0 z-[9999] flex items-center justify-center"
 >
 <div class="absolute inset-0 bg-black/40" @click="cancelDelete" />
 <div
 class="relative bg-n-solid-1 rounded-xl shadow-xl max-w-sm w-full mx-4 p-6"
 >
 <div class="flex items-center gap-3 mb-4">
 <div
 class="flex items-center justify-center size-10 rounded-full bg-red-100 dark:bg-red-500/10"
 >
 <span
 class="i-lucide-alert-triangle size-5 text-red-600 dark:text-red-400"
 />
 </div>
 <div>
 <h3
 class="text-base font-semibold text-n-slate-12"
 >
 Excluir formulario
 </h3>
 <p
 class="text-sm text-n-slate-10 !mb-0"
 >
 Esta ação não pode ser desfeita.
 </p>
 </div>
 </div>
 <p class="text-sm text-n-slate-11 !mb-5">
 Tem certeza que deseja excluir o formulario
 <strong>"{{ deletingForm?.name }}"</strong>? Todas as
 submissoes associadas serao removidas.
 </p>
 <div class="flex items-center justify-end gap-2">
 <button
 class="px-4 py-2 text-sm font-medium text-n-slate-11 bg-n-slate-3 rounded-lg hover:bg-n-alpha-2 transition-colors"
 @click="cancelDelete"
 >
 Cancelar
 </button>
 <button
 class="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors"
 @click="confirmDelete"
 >
 Excluir
 </button>
 </div>
 </div>
 </div>
 </Transition>
 </Teleport>

 <!-- Embed Code Modal -->
 <EmbedCodeModal
 v-if="embedFormId"
 :form-id="embedFormId"
 :form="forms?.find(f => f.id === embedFormId)"
 @close="embedFormId = null"
 />
 </div>
</template>
